import React, { FC, useState, FormEvent } from "react";
import {
  Box,
  SimpleGrid,
  Stack,
  Heading,
  Text,
  Image,
  Button,
  Link,
  Badge,
  useColorMode,
  Input,
} from "@chakra-ui/core";
import { Img } from "@chakra-ui/react";
import { SRLWrapper } from "simple-react-lightbox";
import IProject from "types/project";

interface Props {
  projects: IProject[];
}

const Projects: FC<Props> = ({ projects = [] }) => {
  const { colorMode } = useColorMode();
  const cardBgColor = { light: "white", dark: "gray.900" };
  const cardColor = { light: "gray.900", dark: "white" };
  const [searchQuery, setSearchQuery] = useState("");
  const sortedProjects = projects.filter((project: IProject) =>
    project.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const searchNode = () => {
    return (
      <Box>
        <Input
          value={searchQuery}
          onChange={(e: FormEvent<HTMLInputElement>) =>
            setSearchQuery(e.currentTarget.value)
          }
          placeholder="Search for an project"
        />
      </Box>
    );
  };

  const headingNode = () => {
    return (
      <Box>
        <Stack spacing={2}>
          <Heading as="h1" size="xl">
            Projects
          </Heading>
          <Text>A small selection of our past Projects</Text>
        </Stack>
      </Box>
    );
  };

  const titleNode = (title: string) => {
    return (
      <Heading as="h3" size="md" letterSpacing="tight" lineHeight="tall">
        {title}
      </Heading>
    );
  };

  const imageNode = (image: string, title: string) => {
    if (image) {
      return (
        <SRLWrapper>
          <Img src={image} alt={title} />
        </SRLWrapper>
      );
    } else {
      return null;
    }
  };

  const descriptionNode = (description: string) => {
    return <Text fontSize="sm">{description}</Text>;
  };

  const ctaNode = (url: string, alt: string) => {
    if (url) {
      return (
        <Link href={url} alt={`${url} - ${alt}`} target="_blank">
          <Button
            rightIcon="external-link"
            fontSize="sm"
            size="sm"
            className="projects-button"
          >
            View project
          </Button>
        </Link>
      );
    } else {
      return (
        <Button
          rightIcon="external-link"
          fontSize="sm"
          size="sm"
          className="projects-button-transparent"
        >
          No Link
        </Button>
      );
    }
  };

  const projectsNode = () => {
    if (!sortedProjects.length) {
      return (
        <Stack mx="auto" textAlign="center">
          <Image
            src="/images/common/no-items.svg"
            alt="No projects found!"
            size={64}
          />
          <Text>No projects found!</Text>
        </Stack>
      );
    }

    return (
      <SimpleGrid columns={{ xs: 1, sm: 1, md: 2, lg: 3 }} spacing={8}>
        {/* <Grid templateColumns="repeat(3, 1fr)" gap={8}> */}
        {sortedProjects.map((project: IProject, index: number) => {
          return (
            <Box
              maxW="sm"
              borderWidth="1px"
              borderRadius="lg"
              overflow="hidden"
              key={index}
              bg={cardBgColor[colorMode]}
              color={cardColor[colorMode]}
              rounded="md"
              shadow="md"
              className="projects-box"
            >
              {imageNode(project.image, project.title)}
              <Box
                d="flex"
                mt="2"
                alignItems="right"
                className="projects-button-wrapper"
                justifyContent="flex-end"
                pr="2"
              >
                <Stack direction="row">
                  {/* Todo: add Badges for technology etc.. Project Management, ReactJs, Flutter */}
                  <Box>{ctaNode(project.url, project.title)}</Box>
                </Stack>
              </Box>

              <Box p="6" pt="3">
                <Box mt="0" fontWeight="semibold" as="h4" lineHeight="tight">
                  {titleNode(project.title)}
                </Box>

                <Box mt="2">{descriptionNode(project.description)}</Box>
              </Box>
            </Box>
          );
        })}
      </SimpleGrid>
    );
  };

  return (
    <Stack spacing={8}>
      {headingNode()}
      {searchNode()}
      {projectsNode()}
    </Stack>
  );
};

export default Projects;
